import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, X, Loader2, CheckCheck, AlertTriangle, AlertCircle, Info, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
    fetchCompanyNotifications,
    fetchCompanyUnreadCount,
    markCompanyNotificationRead,
    markAllCompanyNotificationsRead,
} from "@/lib/api";

const POLL_MS = 30_000;

const SEV = {
    critical: { icon: AlertCircle, cls: "text-red-600 bg-red-50" },
    warning: { icon: AlertTriangle, cls: "text-amber-600 bg-amber-50" },
    success: { icon: CheckCircle2, cls: "text-emerald-600 bg-emerald-50" },
    info: { icon: Info, cls: "text-blue-600 bg-blue-50" },
};

/** Company (tenant-scoped) notification bell + slide-over. */
export default function CompanyNotificationBell() {
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const timerRef = useRef(null);
    const navigate = useNavigate();

    const pollCount = useCallback(async () => {
        try { const r = await fetchCompanyUnreadCount(); setCount(Number(r?.count || 0)); }
        catch { /* silent */ }
    }, []);

    useEffect(() => {
        pollCount();
        timerRef.current = setInterval(pollCount, POLL_MS);
        return () => timerRef.current && clearInterval(timerRef.current);
    }, [pollCount]);

    const load = async () => {
        setLoading(true);
        try { const r = await fetchCompanyNotifications({ limit: 50 }); setItems(r?.items || []); setCount(Number(r?.total_unread || 0)); }
        catch { toast.error("Kunne ikke hente notifikationer"); }
        finally { setLoading(false); }
    };

    const openPanel = () => { setOpen(true); load(); };

    const onItemClick = async (n) => {
        try { if (!n.read_at) { await markCompanyNotificationRead(n.id); } } catch { /* ignore */ }
        setOpen(false);
        pollCount();
        if (n.deep_link) navigate(n.deep_link);
    };

    const markAll = async () => {
        try { await markAllCompanyNotificationsRead(); toast.success("Alle markeret som læst"); load(); }
        catch { toast.error("Kunne ikke markere alle"); }
    };

    return (
        <>
            <button
                type="button" onClick={openPanel} data-testid="company-notification-bell"
                className="relative inline-flex items-center justify-center w-10 h-10 rounded-full bg-white border border-slate-200 shadow-sm hover:bg-slate-50 transition"
                aria-label="Notifikationer"
            >
                <Bell className="w-5 h-5 text-slate-700" />
                {count > 0 && (
                    <span data-testid="company-notification-count"
                        className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold inline-flex items-center justify-center">
                        {count > 99 ? "99+" : count}
                    </span>
                )}
            </button>

            {open && (
                <div className="fixed inset-0 z-[70]" data-testid="company-notification-panel">
                    <div className="absolute inset-0 bg-slate-900/30" onClick={() => setOpen(false)} />
                    <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
                        <header className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Bell className="w-4 h-4 text-slate-600" />
                                <h3 className="text-base font-bold text-slate-900">Notifikationer</h3>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={markAll} data-testid="company-notif-mark-all"
                                    className="text-xs inline-flex items-center gap-1 text-slate-600 hover:text-slate-900">
                                    <CheckCheck className="w-3.5 h-3.5" /> Markér alle
                                </button>
                                <button onClick={() => setOpen(false)} className="p-1 hover:bg-slate-100 rounded">
                                    <X className="w-4 h-4 text-slate-500" />
                                </button>
                            </div>
                        </header>
                        <div className="flex-1 overflow-y-auto">
                            {loading ? (
                                <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
                            ) : items.length === 0 ? (
                                <p className="text-center text-sm text-slate-400 py-16">Ingen notifikationer</p>
                            ) : (
                                <ul className="divide-y divide-slate-100">
                                    {items.map((n) => {
                                        const s = SEV[n.severity] || SEV.info;
                                        const Icon = s.icon;
                                        return (
                                            <li key={n.id}>
                                                <button type="button" onClick={() => onItemClick(n)}
                                                    data-testid={`company-notif-item-${n.id}`}
                                                    className={`w-full text-left px-5 py-3 flex gap-3 hover:bg-slate-50 transition ${n.read_at ? "opacity-60" : ""}`}>
                                                    <span className={`w-8 h-8 rounded-lg inline-flex items-center justify-center shrink-0 ${s.cls}`}>
                                                        <Icon className="w-4 h-4" />
                                                    </span>
                                                    <span className="min-w-0 flex-1">
                                                        <span className="flex items-center gap-2">
                                                            <span className="text-sm font-semibold text-slate-900 truncate">{n.title}</span>
                                                            {!n.read_at && <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />}
                                                        </span>
                                                        <span className="block text-xs text-slate-600 mt-0.5">{n.message}</span>
                                                        <span className="block text-[10px] text-slate-400 mt-1">
                                                            {n.created_at ? new Date(n.created_at).toLocaleString("da-DK") : ""}
                                                        </span>
                                                    </span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}
